import React, {useRef} from 'react';
import {I18nextProvider} from 'react-i18next';

import {CartProvider} from '@zooplus/cart-provider';
import {Domain, findSiteByDomain, Stage, SiteProvider} from '@zooplus/commons-lang';

import Constants from '../../common/Constants';
import {CartEventsProvider} from '../../common/contexts/CartEventsContext';
import {FeaturesProvider} from '../../common/contexts/FeaturesContext';
import {LocResourcesProvider} from '../../common/contexts/LocResourcesContext';
import {RewardShopProvider} from '../../common/contexts/RewardShopContext';
import ErrorHandler from '../../common/errors/ErrorHandler';
import {initializeI18n} from '../../common/services/i18n-service';
import MetricsService from '../../common/services/MetricsService';
import {View} from '../../common/utils/types';
import {FiltersProvider} from '../contexts/FiltersContext';
import keys from '../utils/locResourcesKeys';

import RewardShopComponent from './RewardShopComponent';
import {getLocale} from "../../common/utils/locale";

interface RewardShopProps {
  siteDomain: Domain;
  customerId: number | null;
  view: View;
  environment: Stage;
  bpEnabled: boolean;
}

const RewardShop = ({
  siteDomain,
  customerId,
  view,
  environment,
  bpEnabled,
}: RewardShopProps): JSX.Element => {
  const metricServiceInstanceRef = useRef<MetricsService>(
    MetricsService.init(Constants.COMPONENT_NAME.REWARD_SHOP, Constants.COMPONENT_LOCATION.REWARD_SHOP, siteDomain)
  );

  const site = findSiteByDomain(siteDomain);
  return (
    <ErrorHandler metricsService={metricServiceInstanceRef.current}>
      <LocResourcesProvider
        metricsService={metricServiceInstanceRef.current}
        locale={getLocale(site)}
        keys={keys}
        siteId={site.id}>
        <RewardShopProvider
          siteDomain={site.domain}
          view={view}
          metricsService={metricServiceInstanceRef.current}>
          <SiteProvider site={site} locale={site.locale}>
            <I18nextProvider i18n={initializeI18n(site.locale)}>
              <CartProvider stage={environment}>
                <CartEventsProvider view={view}>
                  <FiltersProvider>
                    <FeaturesProvider customerId={customerId} siteDomain={siteDomain}>
                      <RewardShopComponent
                        view={view}
                        siteDomain={siteDomain}
                        bpEnabled={bpEnabled}
                      />
                    </FeaturesProvider>
                  </FiltersProvider>
                </CartEventsProvider>
              </CartProvider>
            </I18nextProvider>
          </SiteProvider>
        </RewardShopProvider>
      </LocResourcesProvider>
    </ErrorHandler>
  );
};

export default React.memo(RewardShop);
